import { FormFieldsEnum, type Customer, type EmailPayload, type EquipmentInput, type ReachRequest, type ServiceEvent } from '@/types'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
function phoneCheck(v: any) {
  return !v.driver_phone && v.contact_phone && v.contact_phone != '' ? v.contact_phone
    : v.driver_phone && v.driver_phone != '' ? v.driver_phone
      : '9999999999';
}
// Mirrors ers-api's Utils/PhoneSanitizer.cs for the explicit-marker and country-code
// cases. Unlike the backend, this treats unmarked overflow (extra digits with no
// "x"/"ext" marker) as an implied extension rather than throwing -- the backend still
// throws on that case as a defensive backstop for anything calling the API directly.
const EXPLICIT_EXTENSION_REGEX = /(?:ext\.?|extension|x|#)\s*[:.\-]?\s*(\d{1,6})\s*$/i
const EXPECTED_PHONE_LENGTH = 10

function sanitizePhone(raw?: string): { phone: string; extension?: string } {
  if (!raw || !raw.trim()) return { phone: '' }

  let trimmed = raw.trim()
  let extension: string | undefined

  const explicitMatch = trimmed.match(EXPLICIT_EXTENSION_REGEX)
  if (explicitMatch) {
    extension = explicitMatch[1]
    trimmed = trimmed.slice(0, explicitMatch.index)
  }

  let digitsOnly = trimmed.replace(/\D/g, '')

  if (digitsOnly.length === EXPECTED_PHONE_LENGTH + 1 && digitsOnly.startsWith('1')) {
    digitsOnly = digitsOnly.slice(1)
  }

  if (digitsOnly.length > EXPECTED_PHONE_LENGTH) {
    // No explicit marker was found, but there are extra digits beyond a standard
    // 10-digit number -- treat the overflow as an implied extension rather than
    // rejecting the submission.
    extension ??= digitsOnly.slice(EXPECTED_PHONE_LENGTH)
    digitsOnly = digitsOnly.slice(0, EXPECTED_PHONE_LENGTH)
  }

  return { phone: digitsOnly, extension }
}
function emailCheck(email:any){
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}
export async function reachObjBuilder(v: { [key: string]: any }): Promise<ReachRequest> {
  const d = new Date()

  // Sanitize both phone fields up front -- strips formatting characters and
  // separates out any extension (explicit "x123"/"ext 123", or leftover digits
  // beyond 10) so it never ends up baked into the number sent to the vendor.
  const driverPhoneResult = sanitizePhone(v.driver_phone)
  const contactPhoneResult = sanitizePhone(v.contact_phone)
  // Some customer forms may add a dedicated extension field alongside the phone
  // number itself, rather than the driver typing "x123" inline.
  const driverPhoneExt = driverPhoneResult.extension ?? (v.driver_phone_ext ? String(v.driver_phone_ext).replace(/\D/g, '') : undefined)
  const contactPhoneExt = contactPhoneResult.extension ?? (v.contact_phone_ext ? String(v.contact_phone_ext).replace(/\D/g, '') : undefined)

  var phone: string = ''
  phone = phoneCheck({ driver_phone: driverPhoneResult.phone, contact_phone: contactPhoneResult.phone })

  let serviceNeededByIso: string | null = null;
  if (v.service_needed_by) {
    const parsedDate = new Date(v.service_needed_by);
    // Check if the date is actually valid before calling toISOString()
    serviceNeededByIso = !isNaN(parsedDate.getTime()) 
      ? parsedDate.toISOString() 
      : null;
  }
const extraNotes: string[] = []

if (v.reference_number) extraNotes.push(`Ref#: ${v.reference_number}`)
if (v.description) extraNotes.push(`Description: ${v.description}`)
if (v.plate_number_and_state) extraNotes.push(`Plate: ${v.plate_number_and_state}`)
// The vendor's phone field has no room for an extension, so it goes in notes
// instead of being silently dropped or causing the submission to be rejected
// for being too long. (ers-api re-derives this independently as well.)
if (driverPhoneExt) extraNotes.push(`Driver Phone Ext: ${driverPhoneExt}`)
if (contactPhoneExt) extraNotes.push(`Contact Phone Ext: ${contactPhoneExt}`)

const breakdownNotes = [v.notes ?? '', ...extraNotes].filter(Boolean).join(' | ')
  const obj: ReachRequest = {
    customerName: v.customerName,
    accountID: v.account_number ? formatAccountNumber(v.account_number) : "999999",
    driverName: v.driver_name ?? "Unknown",
    driverPhone: driverPhoneResult.phone || phone,
    initialContactTime: d,
    location: {
      latitude: v.latitude ?? "0.0",
      longitude: v.longitude ?? "0.0",
      address: v.address ?? v.location ?? "",
      locationType: getLocationType(v.location_type ?? "Road Side"),
      locationDescription: v.location_notes
    },
    breakdownNotes: breakdownNotes,
    contactName: v.contact_name ?? "No Name Provided",
    contactPhone: contactPhoneResult.phone || phone,
    driverEmail: emailCheck(v.driver_email) ? v.driver_email : "NoEmailProvided@pompstire.com",
    contactEmail: emailCheck(v.contact_email) ? v.contact_email : "NoEmailProvided@pompstire.com",
    // Some customer forms configure the equipment type field as "unit_type" or
    // "equipment_type" instead of "vehicle_type", and the identifier field as
    // "container_number" instead of "unit_number" -- same concepts, different
    // Firestore field names per customer. See firestore-audit-report.json.
    equipmentInput: buildEquipmentInput(v.unit_number ?? v.container_number, v.chassis_number ?? "", v.tractor_number ?? "", v.trailer_number, v.vehicle_type ?? v.unit_type ?? v.equipment_type ?? "", v.axle_count ?? "2"),
    serviceEvents: getServiceEvents(v['wheel_position(s)'], v.tire_size ?? "", v.wheel_material ?? "STEEL", v.tire_tread_preference ?? "",v.tire_reason ?? "", v.wheel_reason ?? "Wheel Off", v.mudflap_reason ?? "N/A", v.pilot_type ?? "HUB_PILOT"),
    initialContactDate: d,
    // Some customer forms configure this field as "po_#" instead of "purchase_order_number".
    pOnumber: v.purchase_order_number ?? v['po_#'] ?? "",
    licensePlate: v.plate_number_and_state ?? "",
    dotNumber: v.vin_number ?? "",
    odometer: v.odometer ?? "",
    hubometer: v.hubometer ?? "",
    vehicleModel: v.vehicle_make_model ?? "",
    serviceNeededBy: serviceNeededByIso ?? null,
  }

  return obj
}

// Return location type formatted for API
function getLocationType(v: string): string {
  if (v == 'Road Side')
    v = 'roadside'

  if (v == 'In Bay')
    v = 'in_bay'

  if (v == 'Yard Check')
    v = 'yard_check'

  return v
}

// Create Service Events Array of Objects
type WP = {
  Position: string
  Label: string
}

function getServiceEvents(w: WP[], ts: string, wm: string, tp: string,tr:string , wtr: string, mfr: string, wpt: string): ServiceEvent[] {
  let se: ServiceEvent[] = []
  if (!w || w.length == 0) {
    se.push({
        wheelPosition: "LS",
        wheelSize: "12R22.5",
        treadPattern: tp,
        tireReason: "",
        wheelPilotType: "",
        wheelMaterialType: "",
        wheelTypeReason: "",
        mudflapReason: ""
      })
    
    return se
  }
  let len = w.length
  for (let i = 0; i < len; i++) {

    se.push({
      wheelPosition: w[i].Position,
      wheelSize: ts,
      treadPattern: tp,
      tireReason:tr,
      wheelPilotType: wpt,
      wheelMaterialType: wm,
      wheelTypeReason: wtr,
      mudflapReason: mfr
    })
  }
  return se
}

// Collects raw equipment identifiers as entered by the user.
// All type inference, field mapping, and chassis-format validation
// now happen server-side in ers-api (EquipmentMappingService).
function buildEquipmentInput(
  unit_number?: string,
  chassis_number?: string,
  tractor_number?: string,
  trailer_number?: string,
  vehicle_type?: string,
  axle_count?: string,
): EquipmentInput {
  return {
    unit_number,
    chassis_number,
    tractor_number,
    trailer_number,
    vehicle_type,
    axle_count,
  }
}

function formatAccountNumber(an: string): string {
  if (an.includes('|')) {
    let split_an = an.split('|')
    an = split_an[1]
  }
  return an.trim()
}

export function buildEmailPayload(values: Record<string, unknown>, customerName?: string): EmailPayload {
  const toRecipients: string[] = []
  const emailFields = ['driver_email', 'contact_email', 'alternate_email', 'alternate_email2']

  emailFields.forEach(field => {
    const v = values[field]
    if (typeof v === 'string' && v.includes('@')) {
      toRecipients.push(v)
    }
  })

  return {
    sender: 'nomonitor@pompstire.com',
    to: toRecipients,
    cc: [],
    subject: `Service Dispatch Event: ${customerName ?? ''}`,
    body: createHTMLBody(values),
    attachment: [],
    attachmentData: [],
    bodyIsHTML: true,
  }
}

// Create HTML Body for form to Email
export function createHTMLBody(obj: Record<string, any>): string {
  let html = '';
  let i = 1;
  let id;
  for (const [key, value] of Object.entries(obj)) {
    if (key == 'reachEventID') { id = value }
    if (key == 'wheel_position(s)' && typeof value === 'object' && value !== null) {
      for (const [key2, value2] of Object.entries(value)) {
        for (const [key3, value3] of Object.entries(value2)) {
          if (key3 == 'Label') {
            html += `<td><strong>${key}:</strong></td><td>${JSON.stringify(value3)}</td>`;
            if (i % 2 === 0) {
              html += '</tr><tr>';
            }
            i++;
          }
        }
      }
    }
    else if (typeof value === 'object' && value !== null && key != 'reachEventID') {
      html += `<td><strong>${key}:</strong></td><td>${JSON.stringify(value)}</td>`;
      if (i % 2 === 0) {
        html += '</tr><tr>';
      }
      i++;
    } else if (key != 'reachEventID') {
      html += `<td><strong>${key}:</strong></td><td> ${value}</td>`;
      if (i % 2 === 0) {
        html += '</tr><tr>';
      }
      i++;
    }
  }
  html = `<html><h4>Service Dispatch Event Details </h4><body><table><tr> ${html} </tr></table></body></html>`;
  return html;
}